import {css, TextField, useTheme} from '@mui/material';
import {debounce, isEqual, isNumber} from 'lodash';
import * as React from 'react';
import {useEffect, useMemo, useState} from 'react';
import {FormattedMessage} from 'react-intl';
import {GetStylesFunction} from 'src/js/common/types';

interface OwnProps {
  activePage: number;
  pages: number;
  onChange: (page: number) => void;
  showPageLabel?: boolean;
  isBig?: boolean;
}

type Props = OwnProps;

export const PageInput: React.FC<Props> = (props) => {
  const {activePage, pages, onChange, showPageLabel, isBig} = props;
  const [textBoxPage, setTextBoxPage] = useState<number | string>(activePage);

  useEffect(() => {
    if (isNumber(activePage)) {
      setTextBoxPage(activePage);
    }
  }, [activePage]);

  const theme = useTheme();
  const styles = getStyles({theme, params: {isBig}});

  const debouncedChangePage = useMemo(
    () => debounce(onChange, 1000).bind(this),
    [onChange]
  );

  const handleChange = (eventObject) => {
    // do not set page to 1 if the value is empty string
    if (eventObject.target.value !== '') {
      let pageNumber = Number.parseInt(eventObject.target.value, 10);
      // change invalid small page number to the first page
      pageNumber = Math.max(
        // change invalid big page number to the last page
        Math.min(
          // change NaN to the first page
          Number.isNaN(pageNumber) ? 1 : pageNumber,
          pages
        ),
        1
      );
      if (isNumber(pageNumber)) {
        setTextBoxPage(pageNumber);
        if (!isEqual(pageNumber, textBoxPage)) {
          debouncedChangePage(pageNumber);
        }
      }
    } else {
      setTextBoxPage('');
    }
  };

  return (
    <span css={styles.dlPageInput}>
      {showPageLabel && (
        <span data-name={'page-input--go-to-page'} css={styles.pageInputLabel}>
          <FormattedMessage
            id={'common.pageInput.goToPage'}
            defaultMessage={'Go to page'}
            description={'label in page input for navigating to a page'}
          />
        </span>
      )}
      <TextField
        data-name={'page-input--number-input'}
        disabled={pages <= 1}
        variant={'standard'}
        css={styles.textField}
        sx={{
          width: `${
            19 + (isBig ? 8 : 0) + textBoxPage.toString().length * 5
          }px`,
          borderRadius: isBig ? 2 : 1,
        }}
        inputProps={{
          inputMode: 'numeric',
          pattern: '[0-9]*',
          style: {
            textAlign: 'center',
            ...(isBig
              ? {
                  padding: 0,
                }
              : {}),
          },
        }}
        value={textBoxPage}
        onChange={handleChange}
      />
    </span>
  );
};

PageInput.displayName = 'PageInput';

const getStyles: GetStylesFunction = (options) => {
  return {
    textField: css`
      height: ${options?.params?.isBig ? 32 : 24}px;
      background-color: #eeeeee;
      justify-content: center;
    `,
    dlPageInput: css`
      margin-right: ${options?.theme?.spacing(1)};
      display: flex;
      align-items: center;
    `,
    pageInputLabel: css`
      margin-right: ${options?.theme?.spacing(2)};
    `,
  };
};
